import { X, MoreVertical, Pencil, Trash2, Ban, ChevronLeft, ChevronRight, CheckCircle2, Clock, XCircle, ClipboardList, FileText, ArrowLeft, Info, History, Folder, TrendingUp, Plus, CalendarOff, Check } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { directTriggerBookingStatus } from '../../lib/automations/directTrigger';
import { updateBooking } from '../../lib/bookings';
import ColorPickerModal from '../ColorPickerModal';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';

// Debug flag to control logging - set to false to disable logs
const enableHeaderDebug = false;

const statuses = [
  { value: 'created', label: 'Created' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

interface BookingHeaderProps {
  bookingNumber: number;
  title: string;
  status: string;
  onClose: () => void;
  onEdit?: () => void;
  onEditColor?: () => void;
  /** When set, shows a button to schedule a field quote that auto-links to the booking. */
  onScheduleFieldQuote?: () => void;
  onDelete?: () => void;
  onCancel?: () => void;
  /** When set, shows an option to move the booking to the unscheduled state. */
  onUnschedule?: () => void;
  onConvertToBooking?: () => void;
  isBooked?: boolean;
  completedAt?: string | null;
  onStatusChange?: (status: string) => void;
  onCompletedAtChange?: (completedAt: string) => void;
  readOnly?: boolean;
  isMobile?: boolean;
  bookingId?: string;
  businessId?: string;
  canEdit?: boolean;
  // Damage and waiver props
  isLiabilityWaiverEnabled?: boolean;
  liabilityWaiverAgreed?: boolean;
  hasDamageReport?: boolean;
  onSendLiabilityWaiver?: () => void;
  onAddDamageReport?: () => void;
  isSendingLiabilityWaiver?: boolean;
  liabilityWaiverPdfUrl?: string;
  canAccessLiabilityWaiver?: boolean;
  onCreateWarranty?: () => void;
  onViewInvoice?: () => void;
  bookingColor?: string;
  bookingSecondaryColor?: string | null;
  onUpdate?: () => void;
  bookingCreatedBy?: string | null;
  // Linked project props
  linkedProject?: { id: string; title: string } | null;
  onViewProject?: () => void;
  // Linked opportunity props
  linkedOpportunity?: { id: number; title: string; value: number; status: string } | null;
  onViewOpportunity?: () => void;
  // "Remove from automations" toggle
  automationsDisabled?: boolean;
  onToggleAutomationsDisabled?: (next: boolean) => void;
  /**
   * Base z-index for the portaled 3-dots menu (backdrop). Defaults to 100000.
   * Raise this when the host modal is itself elevated (e.g. BookingInfoModal
   * opened above CustomerDetailsModal) so the menu renders above it.
   */
  overlayBaseZIndex?: number;
}

export default function BookingHeader({
  bookingNumber,
  title,
  status,
  onClose,
  onEdit,
  onEditColor,
  onScheduleFieldQuote,
  onDelete,
  onCancel,
  onUnschedule,
  onConvertToBooking,
  isBooked,
  completedAt,
  onStatusChange,
  onCompletedAtChange,
  readOnly = false,
  isMobile = false,
  bookingId,
  businessId,
  canEdit = true,
  // Damage and waiver props
  isLiabilityWaiverEnabled = false,
  liabilityWaiverAgreed = false,
  hasDamageReport = false,
  onSendLiabilityWaiver,
  onAddDamageReport,
  isSendingLiabilityWaiver = false,
  liabilityWaiverPdfUrl,
  canAccessLiabilityWaiver = true,
  onCreateWarranty,
  onViewInvoice,
  bookingColor,
  bookingSecondaryColor,
  onUpdate,
  linkedProject,
  onViewProject,
  linkedOpportunity,
  onViewOpportunity,
  automationsDisabled = false,
  onToggleAutomationsDisabled,
  overlayBaseZIndex = 100000
}: BookingHeaderProps) {
  const [isMenuOpen, setIsMenuOpenState] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState(title);
  const [editColor, setEditColor] = useState(bookingColor || '#3b82f6');
  const [editSecondaryColor, setEditSecondaryColor] = useState<string | undefined>(bookingSecondaryColor || undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [showCancelInfo, setShowCancelInfo] = useState(false);
  const [showDeleteInfo, setShowDeleteInfo] = useState(false);

  // Wrapper function to log all menu state changes
  const setIsMenuOpen = React.useCallback((value: boolean) => {
    const stack = new Error().stack;
    console.log(`🔴 setIsMenuOpen(${value}) called from:`, {
      value,
      stack: stack?.split('\n').slice(1, 5).join('\n') // First 4 lines of stack
    });
    setIsMenuOpenState(value);
  }, []);

  // Debug: Track menu state changes
  useEffect(() => {
    console.log('🟡 isMenuOpen changed:', isMenuOpen);
    if (!isMenuOpen) {
      console.log('🟡 Menu closed');
    }
  }, [isMenuOpen]);

  // Debug: Track edit mode changes
  useEffect(() => {
    console.log('🟡 isEditMode changed:', isEditMode);
  }, [isEditMode]);

  const menuRef = useRef<HTMLDivElement>(null);
  const desktopMenuRef = useRef<HTMLDivElement>(null);
  const [isHistoryMode, setIsHistoryMode] = useState(false);
  
  // Debug: Track history mode changes
  useEffect(() => {
    console.log('🟡 isHistoryMode changed:', isHistoryMode);
  }, [isHistoryMode]);
  const [bookingHistory, setBookingHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Debug: Log menu state changes
  useEffect(() => {
    console.log('📊 Menu state changed:', {
      isMenuOpen,
      isMobile,
      canEdit,
      desktopMenuRefExists: !!desktopMenuRef.current
    });
  }, [isMenuOpen, isMobile, canEdit]);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isUnscheduleModalOpen, setIsUnscheduleModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [unscheduleLoading, setUnscheduleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditingTimestamp, setIsEditingTimestamp] = useState(false);
  const [editTimestamp, setEditTimestamp] = useState('');

  // Status navigation logic
  const currentStatusIndex = statuses.findIndex(s => s.value === status);
  const canStatusGoUp = currentStatusIndex > 0;
  const canStatusGoDown = currentStatusIndex < statuses.length - 1;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'created':
        return 'text-gray-400';
      case 'scheduled':
        return 'text-blue-400';
      case 'in_progress':
        return 'text-yellow-400';
      case 'completed':
        return 'text-green-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-3 w-3" />;
      case 'cancelled':
        return <XCircle className="h-3 w-3 text-red-400" />;
      default:
        return <Clock className="h-3 w-3" />;
    }
  };

  const handleStatusUp = async () => {
    if (canStatusGoUp && onStatusChange && !readOnly && canEdit) {
      const newStatus = statuses[currentStatusIndex - 1].value;
      
      // Only certain statuses should trigger automations
      const shouldTriggerAutomation = newStatus === 'in_progress' || newStatus === 'completed';
      
      if (enableHeaderDebug) {
        console.log(`[BOOKING HEADER] 🔍 Status change: ${status} → ${newStatus}`);
        console.log(`[BOOKING HEADER] 🔍 Should trigger automation: ${shouldTriggerAutomation}`);
        console.log(`[BOOKING HEADER] 🔍 Booking ID available: ${!!bookingId}`);
        console.log(`[BOOKING HEADER] 🔍 Business ID available: ${!!businessId}`);
      }
      
      // First call the parent's onStatusChange to update the database
      onStatusChange(newStatus);
      
      // If it's a status that should trigger automations and we have the booking/business ID
      if (shouldTriggerAutomation && bookingId && businessId) {
        if (enableHeaderDebug) {
          console.log(`[BOOKING HEADER] 🚀 Directly triggering automation for status: ${newStatus}`);
          console.log(`[BOOKING HEADER] 📋 Booking ID: ${bookingId}`);
          console.log(`[BOOKING HEADER] 📋 Business ID: ${businessId}`);
        }
        
        try {
          const result = await directTriggerBookingStatus(bookingId, newStatus, businessId);
          
          if (result.success) {
            if (enableHeaderDebug) {
              console.log(`[BOOKING HEADER] ✅ Successfully triggered ${result.results?.length || 0} actions`);
              // Log each action result for debugging
              if (result.results && result.results.length > 0) {
                result.results.forEach((actionResult, index) => {
                  console.log(`[BOOKING HEADER] 📋 Action ${index + 1}: Type=${actionResult.type}, Success=${actionResult.success}`);
                  if (!actionResult.success && actionResult.error) {
                    console.error(`[BOOKING HEADER] ❌ Action error:`, actionResult.error);
                  }
                });
              } else {
                console.log(`[BOOKING HEADER] ⚠️ No actions were triggered. Check if automations are configured correctly.`);
              }
            }
          } else {
            console.error(`[BOOKING HEADER] ❌ Failed to trigger automations:`, result.error);
          }
        } catch (error) {
          console.error(`[BOOKING HEADER] ❌ Error triggering automation:`, error);
        }
      }
    }
  };

  const handleStatusDown = async () => {
    if (canStatusGoDown && onStatusChange && !readOnly && canEdit) {
      const newStatus = statuses[currentStatusIndex + 1].value;
      
      // Only certain statuses should trigger automations
      const shouldTriggerAutomation = newStatus === 'in_progress' || newStatus === 'completed';
      
      if (enableHeaderDebug) {
        console.log(`[BOOKING HEADER] 🔍 Status change: ${status} → ${newStatus}`);
        console.log(`[BOOKING HEADER] 🔍 Should trigger automation: ${shouldTriggerAutomation}`);
        console.log(`[BOOKING HEADER] 🔍 Booking ID available: ${!!bookingId}`);
        console.log(`[BOOKING HEADER] 🔍 Business ID available: ${!!businessId}`);
      }
      
      // First call the parent's onStatusChange to update the database
      onStatusChange(newStatus);
      
      // If it's a status that should trigger automations and we have the booking/business ID
      if (shouldTriggerAutomation && bookingId && businessId) {
        if (enableHeaderDebug) {
          console.log(`[BOOKING HEADER] 🚀 Directly triggering automation for status: ${newStatus}`);
          console.log(`[BOOKING HEADER] 📋 Booking ID: ${bookingId}`);
          console.log(`[BOOKING HEADER] 📋 Business ID: ${businessId}`);
        }
        
        try {
          const result = await directTriggerBookingStatus(bookingId, newStatus, businessId);
          
          if (result.success) {
            if (enableHeaderDebug) {
              console.log(`[BOOKING HEADER] ✅ Successfully triggered ${result.results?.length || 0} actions`);
              // Log each action result for debugging
              if (result.results && result.results.length > 0) {
                result.results.forEach((actionResult, index) => {
                  console.log(`[BOOKING HEADER] 📋 Action ${index + 1}: Type=${actionResult.type}, Success=${actionResult.success}`);
                  if (!actionResult.success && actionResult.error) {
                    console.error(`[BOOKING HEADER] ❌ Action error:`, actionResult.error);
                  }
                });
              } else {
                console.log(`[BOOKING HEADER] ⚠️ No actions were triggered. Check if automations are configured correctly.`);
              }
            }
          } else {
            console.error(`[BOOKING HEADER] ❌ Failed to trigger automations:`, result.error);
          }
        } catch (error) {
          console.error(`[BOOKING HEADER] ❌ Error triggering automation:`, error);
        }
      }
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      
      console.log('🟡 Click outside handler triggered');
      console.log('🟡 Target:', target);
      console.log('🟡 menuRef.current:', menuRef.current);
      console.log('🟡 desktopMenuRef.current:', desktopMenuRef.current);
      
      // Check if click is inside mobile menu button
      const isInsideMobileMenuButton = menuRef.current && menuRef.current.contains(target);
      
      // Check if click is inside mobile menu portal (rendered to document.body)
      const mobileMenuPortal = document.querySelector('[data-mobile-menu]');
      const isInsideMobileMenuPortal = mobileMenuPortal && mobileMenuPortal.contains(target);
      
      const isInsideMobileMenu = isInsideMobileMenuButton || isInsideMobileMenuPortal;
      
      // Check if click is inside desktop menu button
      const isInsideDesktopMenuButton = desktopMenuRef.current && desktopMenuRef.current.contains(target);
      
      // Check if click is inside desktop menu portal (rendered to document.body)
      const desktopMenuPortal = document.querySelector('[data-desktop-menu]');
      const isInsideDesktopMenuPortal = desktopMenuPortal && desktopMenuPortal.contains(target);
      
      const isInsideDesktopMenu = isInsideDesktopMenuButton || isInsideDesktopMenuPortal;
      
      console.log('🟡 isInsideMobileMenuButton:', isInsideMobileMenuButton);
      console.log('🟡 mobileMenuPortal:', !!mobileMenuPortal);
      console.log('🟡 isInsideMobileMenuPortal:', isInsideMobileMenuPortal);
      console.log('🟡 isInsideMobileMenu (combined):', isInsideMobileMenu);
      console.log('🟡 isInsideDesktopMenuButton:', isInsideDesktopMenuButton);
      console.log('🟡 desktopMenuPortal:', !!desktopMenuPortal);
      console.log('🟡 isInsideDesktopMenuPortal:', isInsideDesktopMenuPortal);
      console.log('🟡 isInsideDesktopMenu (combined):', isInsideDesktopMenu);
      
      // Only close if click is outside both menus
      if (!isInsideMobileMenu && !isInsideDesktopMenu) {
        console.log('🟡 Closing menu - click was outside both menus');
        setIsMenuOpen(false);
        setIsEditMode(false);
        setShowCancelInfo(false);
        setShowDeleteInfo(false);
      } else {
        console.log('🟡 Keeping menu open - click was inside a menu');
      }
    }

    if (isMenuOpen) {
      console.log('🟢 Menu is open, setting up click-outside handler');
      // Use a small delay to allow the click event that opened the menu to complete first
      const timeoutId = setTimeout(() => {
        console.log('🟢 Attaching click-outside listener');
        document.addEventListener('mousedown', handleClickOutside, true);
      }, 100);
      
      return () => {
        console.log('🟢 Cleaning up click-outside listener');
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    } else {
      console.log('🔴 Menu is closed');
    }
  }, [isMenuOpen]);

  // Reset edit state when menu closes
  useEffect(() => {
    if (!isMenuOpen) {
      setIsEditMode(false);
      setIsHistoryMode(false);
      setEditTitle(title);
      setEditColor(bookingColor || '#3b82f6');
      setEditSecondaryColor(bookingSecondaryColor || undefined);
      setEditError(null);
      setBookingHistory([]);
    }
  }, [isMenuOpen, title, bookingColor, bookingSecondaryColor]);

  // Fetch booking history when history mode is opened
  useEffect(() => {
    if (isHistoryMode && bookingId) {
      fetchBookingHistory();
    }
  }, [isHistoryMode, bookingId]);

  const fetchBookingHistory = async () => {
    if (!bookingId) return;
    
    try {
      setHistoryLoading(true);
      
      // Fetch status history
      const { data: statusHistory } = await supabase
        .from('booking_status_history')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false });

      // Fetch milestones
      const { data: milestones } = await supabase
        .from('booking_milestones')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false });

      // Fetch booking to get created_by
      const { data: booking } = await supabase
        .from('bookings')
        .select('created_by, created_at')
        .eq('id', bookingId)
        .single();

      // Get resource names for creators
      const resourceIds = new Set<string>();
      if (booking?.created_by) resourceIds.add(booking.created_by);
      
      // Fetch resources
      let resourceMap: Record<string, { first_name?: string; last_name?: string; email?: string }> = {};
      if (resourceIds.size > 0) {
        const { data: resources } = await supabase
          .from('resources')
          .select('id, first_name, last_name, email')
          .in('id', Array.from(resourceIds));
        
        if (resources) {
          resources.forEach(resource => {
            resourceMap[resource.id] = {
              first_name: resource.first_name,
              last_name: resource.last_name,
              email: resource.email
            };
          });
        }
      }

      // Combine and sort all history items
      const allHistory: any[] = [];
      
      // Find booking scheduled milestone to combine with creation
      const scheduledMilestone = milestones?.find(m => 
        m.milestone?.type === 'booking_scheduled'
      );
      
      // Add booking creation as first item (combine with scheduled info if available)
      if (booking) {
        const creator = booking.created_by ? resourceMap[booking.created_by] : null;
        const creatorName = creator 
          ? `${creator.first_name || ''} ${creator.last_name || ''}`.trim() || creator.email || 'Unknown'
          : 'Unknown';
        
        let description = 'Booking was created';
        if (scheduledMilestone?.milestone?.metadata) {
          const scheduledDate = scheduledMilestone.milestone.metadata.scheduled_date;
          const scheduledTime = scheduledMilestone.milestone.metadata.scheduled_time;
          if (scheduledDate && scheduledTime) {
            description = `Booking was created and scheduled for ${scheduledDate} at ${scheduledTime}`;
          }
        }
        
        allHistory.push({
          type: 'created',
          status: 'created',
          timestamp: booking.created_at,
          created_by: creatorName,
          description: description
        });
      }
      
      if (statusHistory) {
        statusHistory.forEach(item => {
          // Skip 'scheduled' status if we already have it in the created entry
          if (item.status === 'scheduled' && scheduledMilestone) {
            return;
          }
          allHistory.push({
            type: 'status_change',
            ...item,
            timestamp: item.created_at
          });
        });
      }
      
      if (milestones) {
        milestones.forEach(item => {
          // Skip booking_scheduled milestone since we combined it with creation
          if (item.milestone?.type === 'booking_scheduled') {
            return;
          }
          allHistory.push({
            type: 'milestone',
            ...item,
            timestamp: item.created_at
          });
        });
      }

      // Sort by timestamp (oldest first - chronological order)
      allHistory.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      
      setBookingHistory(allHistory);
    } catch (error) {
      console.error('Error fetching booking history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!bookingId) return;
    
    try {
      setIsSaving(true);
      setEditError(null);
      
      await updateBooking(bookingId, {
        title: editTitle.trim(),
        color: editColor,
        secondary_color: editSecondaryColor || undefined
      });
      
      setIsEditMode(false);
      setIsMenuOpen(false);
      
      // Refresh booking data
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error updating booking:', err);
      setEditError(err instanceof Error ? err.message : 'Failed to update booking');
    } finally {
      setIsSaving(false);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format timestamp for datetime-local input
  const formatTimestampForInput = (timestamp: string) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Handle starting timestamp edit
  const handleEditTimestamp = () => {
    if (completedAt && status === 'completed' && !readOnly && canEdit) {
      setEditTimestamp(formatTimestampForInput(completedAt));
      setIsEditingTimestamp(true);
    }
  };

  // Handle saving timestamp changes
  const handleSaveTimestamp = () => {
    if (editTimestamp && onCompletedAtChange) {
      const newCompletedAt = new Date(editTimestamp).toISOString();
      onCompletedAtChange(newCompletedAt);
      setIsEditingTimestamp(false);
      setEditTimestamp('');
    }
  };

  // Handle canceling timestamp edit
  const handleCancelTimestamp = () => {
    setIsEditingTimestamp(false);
    setEditTimestamp('');
  };

  return (
    <>
      {/* Color Picker Modal */}
      {isColorPickerOpen && (
        <ColorPickerModal
          isOpen={isColorPickerOpen}
          onClose={() => {
            // Only close the color picker, keep edit mode open
            setIsColorPickerOpen(false);
          }}
          onColorSelect={(color, secondaryColor) => {
            // Update colors but keep edit mode open
            setEditColor(color);
            setEditSecondaryColor(secondaryColor);
            setIsColorPickerOpen(false);
          }}
          currentColor={editColor}
          currentSecondaryColor={editSecondaryColor}
        />
      )}

      <div className={`relative ${isMobile ? 'bg-black' : 'bg-black'} border-b ${isMobile ? 'border-gray-800/50' : 'border-gray-200'} shadow-sm`}
        style={{ 
          zIndex: 10,
          width: '100%',
          ...(isMobile && {
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(0, 0, 0, 0.95)'
          })
        }}>
        
        {/* Mobile Header Layout - iOS Style - Compact */}
        <div className={`flex items-start justify-between ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'}`}>
          {/* Left side - Close button */}
          <div className="flex items-center flex-shrink-0">
            <button 
              onClick={onClose} 
              className={`flex items-center justify-center ${isMobile ? 'w-10 h-10' : 'w-8 h-8'} rounded-full text-white transition-all duration-200 active:scale-95 touch-manipulation`}
              style={{ 
                WebkitTapHighlightColor: 'transparent',
                backgroundColor: 'transparent'
              }}
              onMouseDown={(e) => {
                if (navigator.vibrate) navigator.vibrate(10);
              }}
              onTouchStart={(e) => {
                if (navigator.vibrate) navigator.vibrate(10);
              }}
            >
              <X className={`${isMobile ? 'h-6 w-6' : 'h-5 w-5'}`} />
            </button>
          </div>

          {/* Center - Booking number, title, and status - Flexible, can wrap below menu */}
          <div className="flex-1 flex justify-center px-2 min-w-0">
            <div className="flex-1 min-w-0 w-full">
              <div className="text-center">
                {/* Linked Project Badge */}
                {linkedProject && (
                  <button
                    onClick={onViewProject}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-1.5 bg-blue-500/20 hover:bg-blue-500/30 active:bg-blue-500/40 rounded-full transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Folder className="h-3 w-3 text-blue-400" />
                    <span className="text-[11px] font-medium text-blue-300">
                      {linkedProject.title}
                    </span>
                  </button>
                )}
                {/* Linked Opportunity Badge */}
                {linkedOpportunity && (
                  <button
                    onClick={onViewOpportunity}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-1.5 bg-blue-500/20 hover:bg-blue-500/30 active:bg-blue-500/40 rounded-full transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <TrendingUp className="h-3 w-3 text-blue-400 flex-shrink-0" />
                    <span className="text-[11px] font-medium text-blue-300 whitespace-nowrap">
                      {linkedOpportunity.title || 'Opportunity'}
                    </span>
                  </button>
                )}
                <h2 className={`${isMobile ? 'text-[16px] font-semibold' : 'text-lg font-semibold'} text-white truncate`}
                    style={{ 
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                      letterSpacing: '-0.01em',
                      wordBreak: 'normal',
                      overflowWrap: 'normal',
                      hyphens: 'auto'
                    }}>
                  #{bookingNumber} - {title || 'Untitled Booking'}
                </h2>
                {!isMobile && (
                  <div className="flex items-center justify-center mt-0.5">
                    <div className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full mr-1.5 sm:mr-2 shadow-sm ${
                      status === 'completed' ? 'bg-green-500' : 
                      status === 'in_progress' ? 'bg-yellow-500' :
                      status === 'scheduled' ? 'bg-blue-500' :
                      status === 'cancelled' ? 'bg-red-500' :
                      'bg-gray-500'
                    }`} />
                    <span className="text-sm text-gray-300 truncate">
                      {status === 'cancelled' ? 'Booking cancelled' : 
                       status === 'completed' ? 'Booking completed' :
                       status === 'in_progress' ? 'Booking in progress' :
                       status === 'scheduled' ? 'Booking scheduled' :
                       'Booking created'}
                    </span>
                  </div>
                )}
                {/* Mobile Status Bar - Inside Header - iOS Style - Compact */}
                {isMobile && status !== 'cancelled' && (
                  <div className="flex items-center justify-center space-x-1 mt-2 flex-nowrap">
                    {[
                      { value: 'scheduled', label: 'Scheduled' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' }
                    ].map((s, index) => {
                      const statusIndex = ['scheduled', 'in_progress', 'completed'].findIndex(st => st === status);
                      const isActive = index <= statusIndex;
                      const isNext = index === statusIndex + 1;
                      
                      return (
                        <React.Fragment key={s.value}>
                      <button
                            onClick={() => {
                              if (!readOnly && canEdit && (isActive || isNext) && onStatusChange) {
                                if (navigator.vibrate) navigator.vibrate(10);
                                onStatusChange(s.value);
                              }
                            }}
                            disabled={readOnly || !canEdit || (!isActive && !isNext)}
                            className={`text-[12px] font-semibold transition-all duration-200 px-2.5 py-1.5 rounded-[8px] min-w-[70px] whitespace-nowrap touch-manipulation active:scale-[0.96] flex-shrink-0 ${
                              isActive
                                ? status === 'scheduled' && index === 0
                                  ? 'text-blue-600 bg-blue-50 shadow-sm'
                                  : status === 'in_progress' && index <= 1
                                  ? 'text-amber-600 bg-amber-50 shadow-sm'
                                  : status === 'completed' && index <= 2
                                  ? 'text-green-600 bg-green-50 shadow-sm'
                                  : 'text-blue-600 bg-blue-50 shadow-sm'
                                : isNext && !readOnly && canEdit
                                ? 'text-gray-400 bg-white/10 hover:bg-white/15 active:bg-white/20'
                                : 'text-gray-500 bg-white/5'
                            }`}
                            style={{ 
                              touchAction: 'manipulation',
                              WebkitTapHighlightColor: 'transparent',
                              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                              letterSpacing: '-0.01em'
                            }}
                            onMouseDown={(e) => {
                              if (!readOnly && canEdit && (isActive || isNext) && navigator.vibrate) {
                                navigator.vibrate(10);
                              }
                            }}
                            onTouchStart={(e) => {
                              if (!readOnly && canEdit && (isActive || isNext) && navigator.vibrate) {
                                navigator.vibrate(10);
                              }
                            }}
                          >
                            {s.label}
                      </button>
                          {index < 2 && (
                            <ChevronRight className={`h-3 w-3 flex-shrink-0 ${
                              index < statusIndex ? 'text-white/60' : 'text-white/30'
                            }`} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
                        </div>
                      </div>

          {/* Right side - Menu button (Mobile only) */}
          {isMobile && (
            <div className="flex items-center flex-shrink-0">
              {onScheduleFieldQuote && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (navigator.vibrate) navigator.vibrate(10);
                    onScheduleFieldQuote();
                  }}
                  className="inline-flex items-center justify-center w-9 h-9 text-white bg-blue-600 hover:bg-blue-700 rounded-full shadow-sm transition-all duration-200 active:scale-95 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  title="Schedule Field Quote"
                  aria-label="Schedule Field Quote"
                >
                  <Plus className="h-5 w-5" strokeWidth={2.5} />
                </button>
              )}
              {canEdit && (
                <div className="relative" ref={menuRef}>
                      <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (navigator.vibrate) navigator.vibrate(10);
                      setIsMenuOpen(!isMenuOpen);
                    }}
                    className="inline-flex items-center justify-center w-10 h-10 text-white rounded-full transition-all duration-200 active:scale-95 touch-manipulation"
                    style={{ 
                      WebkitTapHighlightColor: 'transparent',
                      backgroundColor: 'transparent'
                    }}
                    title="More Options"
                    onMouseDown={(e) => {
                      if (navigator.vibrate) navigator.vibrate(10);
                    }}
                    onTouchStart={(e) => {
                      if (navigator.vibrate) navigator.vibrate(10);
                    }}
                  >
                    <MoreVertical className="h-6 w-6" />
                      </button>

                {/* Mobile: Centered menu with blurred background */}
                {isMenuOpen && isMobile && createPortal(
                  <>
                    {/* Backdrop Overlay */}
                    <div
                      className="fixed inset-0"
                      style={{
                        zIndex: overlayBaseZIndex,
                        background: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        opacity: 0,
                        animation: 'fadeIn 0.2s ease-out forwards',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        if (e.target === e.currentTarget) {
                          setIsMenuOpen(false);
                        }
                      }}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                    />

                    {/* Centered Menu - iOS Style */}
                    <div
                      data-mobile-menu
                      className="fixed bg-white rounded-[14px] shadow-lg"
                      style={{
                        zIndex: overlayBaseZIndex + 1,
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 'calc(100% - 32px)',
                        maxWidth: '320px',
                        opacity: 0,
                        animation: 'fadeIn 0.2s ease-out 0.05s forwards',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                    >
                      {!isEditMode && !isHistoryMode ? (
                        <div className="py-1" role="menu" aria-orientation="vertical">
                          {onToggleAutomationsDisabled && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onToggleAutomationsDisabled(!automationsDisabled);
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                                onTouchStart={(e) => e.stopPropagation()}
                                className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                                role="menuitemcheckbox"
                                aria-checked={automationsDisabled}
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <span className={`h-5 w-5 mr-3 rounded-[6px] border flex items-center justify-center flex-shrink-0 ${automationsDisabled ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}>
                                  {automationsDisabled && <Check className="h-3.5 w-3.5 text-white" />}
                                </span>
                                <span className="font-normal">Remove from automations</span>
                              </button>
                              <div className="h-px bg-gray-100 mx-4" />
                            </>
                          )}
                          {canEdit && status !== 'cancelled' && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsEditMode(true);
                                setEditTitle(title);
                                setEditColor(bookingColor || '#3b82f6');
                                setEditSecondaryColor(bookingSecondaryColor || undefined);
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              onTouchStart={(e) => e.stopPropagation()}
                              className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                              role="menuitem"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <Pencil className="h-5 w-5 mr-3 text-gray-600" />
                              <span className="font-normal">Edit Booking</span>
                            </button>
                          )}
                          {onViewInvoice && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsMenuOpen(false);
                                setTimeout(() => {
                                  onViewInvoice();
                                }, 100);
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              onTouchStart={(e) => e.stopPropagation()}
                              className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                              role="menuitem"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <FileText className="h-5 w-5 mr-3 text-gray-600" />
                              <span className="font-normal">View Invoice</span>
                            </button>
                          )}
                          {bookingId && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsHistoryMode(true);
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              onTouchStart={(e) => e.stopPropagation()}
                              className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                              role="menuitem"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <History className="h-5 w-5 mr-3 text-gray-600" />
                              <span className="font-normal">Booking History</span>
                            </button>
                          )}
                  {/* Damage Report Button */}
                          {isLiabilityWaiverEnabled && canAccessLiabilityWaiver && onAddDamageReport && (
                    <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsMenuOpen(false);
                                setTimeout(() => {
                                  onAddDamageReport();
                                }, 100);
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              onTouchStart={(e) => e.stopPropagation()}
                              className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                              role="menuitem"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <ClipboardList className={`h-5 w-5 mr-3 ${hasDamageReport ? 'text-green-600' : 'text-red-600'}`} />
                              <span className="font-normal">{hasDamageReport ? 'Edit Damage Report' : 'Add Damage Report'}</span>
                    </button>
                  )}
                  {/* Liability Waiver Button */}
                          {isLiabilityWaiverEnabled && canAccessLiabilityWaiver && (
                            <>
                  {!liabilityWaiverAgreed && onSendLiabilityWaiver ? (
                    <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsMenuOpen(false);
                                    setTimeout(() => {
                                      onSendLiabilityWaiver();
                                    }, 100);
                                  }}
                      disabled={isSendingLiabilityWaiver}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
                                  role="menuitem"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  <ClipboardList className="h-5 w-5 mr-3 text-red-600" />
                                  <span className="font-normal">Send Waiver</span>
                    </button>
                  ) : liabilityWaiverPdfUrl ? (
                    <a
                      href={liabilityWaiverPdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsMenuOpen(false);
                                    setTimeout(() => {
                                      window.open(liabilityWaiverPdfUrl, '_blank');
                                    }, 100);
                                  }}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                                  role="menuitem"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  <ClipboardList className="h-5 w-5 mr-3 text-green-600" />
                                  <span className="font-normal">View Waiver</span>
                    </a>
                  ) : null}
                            </>
                          )}
                          {onCreateWarranty && status === 'completed' && (
                        <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsMenuOpen(false);
                                setTimeout(() => {
                                  onCreateWarranty();
                                }, 100);
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              onTouchStart={(e) => e.stopPropagation()}
                              className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                              role="menuitem"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <span className="font-normal">Schedule Warranty</span>
                        </button>
                          )}
                          {onUnschedule && status !== 'cancelled' && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsMenuOpen(false);
                                setTimeout(() => {
                                  setIsUnscheduleModalOpen(true);
                                }, 100);
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              onTouchStart={(e) => e.stopPropagation()}
                              className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                              role="menuitem"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <CalendarOff className="h-5 w-5 mr-3 text-amber-600" />
                              <span className="font-normal">Unschedule</span>
                            </button>
                          )}
                          {(onCancel || onDelete) && (
                            <>
                              <div className="h-[0.5px] bg-gray-200 mx-4 my-1"></div>
                              <div className="flex gap-2 px-4 pb-1">
                                {onCancel && (
                                  <div className="flex-1 relative">
                        <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsMenuOpen(false);
                                        setTimeout(() => {
                                          setIsCancelModalOpen(true);
                                        }, 100);
                                      }}
                                      onMouseDown={(e) => e.preventDefault()}
                                      onTouchStart={(e) => e.stopPropagation()}
                                      className={`w-full px-4 py-3.5 text-[17px] text-left flex items-center transition-colors active:bg-gray-100 touch-manipulation relative ${
                                        status === 'cancelled' 
                                          ? 'text-blue-600' 
                                          : 'text-red-600'
                                      }`}
                                      role="menuitem"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      <Ban className="h-5 w-5 mr-3" />
                                      <span className="font-normal flex-1">{status === 'cancelled' ? 'Schedule' : 'Cancel'}</span>
                        </button>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowCancelInfo(!showCancelInfo);
                                        setShowDeleteInfo(false);
                                      }}
                                      onMouseDown={(e) => e.preventDefault()}
                                      onTouchStart={(e) => e.stopPropagation()}
                                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      <Info className="h-4 w-4" />
                                    </button>
                                    {showCancelInfo && (
                                      <div className="absolute top-full left-0 right-0 mt-1 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                                        Changes booking status to canceled and booking remains in your calender with the updated status
                      </div>
              )}
                    </div>
                                )}
                                {onDelete && (
                                  <div className="flex-1 relative">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setIsMenuOpen(false);
                                        setTimeout(() => {
                                          setIsDeleteModalOpen(true);
                                        }, 100);
                                      }}
                                      className="w-full px-4 py-3.5 text-[17px] text-left text-red-600 flex items-center transition-colors active:bg-red-50 touch-manipulation relative"
                                      role="menuitem"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      <Trash2 className="h-5 w-5 mr-3" />
                                      <span className="font-normal flex-1">Delete</span>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowDeleteInfo(!showDeleteInfo);
                                        setShowCancelInfo(false);
                                      }}
                                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      <Info className="h-4 w-4" />
                                    </button>
                                    {showDeleteInfo && (
                                      <div className="absolute top-full left-0 right-0 mt-1 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                                        Permanently deletes booking
                    </div>
                  )}
                </div>
              )}
            </div>
                            </>
                          )}
          </div>
                      ) : isEditMode ? (
                        <div className="p-4 space-y-4">
                          {/* Back Button */}
              <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsEditMode(false);
                              setEditError(null);
                            }}
                            className="flex items-center text-gray-600 hover:text-gray-900 mb-2"
                          >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            <span className="text-sm">Back</span>
              </button>

                          {/* Error Message */}
                          {editError && (
                            <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-sm">
                              {editError}
                            </div>
                          )}

                          {/* Title Input */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Booking Title
                            </label>
                      <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              className="w-full h-11 px-3 rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-base"
                              placeholder="Enter booking title"
                              autoFocus
                            />
                          </div>

                          {/* Color Picker */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Booking Color
                            </label>
                  <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsColorPickerOpen(true);
                              }}
                              className="flex items-center gap-3 w-full p-3 rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                            >
                              {/* Current Color Display */}
                              <div className="w-12 h-12 rounded-lg border-2 border-gray-300 overflow-hidden shadow-sm flex-shrink-0">
                                {editSecondaryColor ? (
                                  <div className="w-full h-full flex">
                                    <div 
                                      className="w-1/2 h-full"
                                      style={{ backgroundColor: editColor }}
                                    />
                                    <div 
                                      className="w-1/2 h-full"
                                      style={{ backgroundColor: editSecondaryColor }}
                                    />
                                  </div>
                                ) : (
                                  <div 
                                    className="w-full h-full"
                                    style={{ backgroundColor: editColor }}
                                  />
                                )}
                              </div>
                              <div className="flex-1 text-left">
                                <div className="text-sm font-medium text-gray-900">
                                  {editSecondaryColor ? 'Two-tone Color' : 'Solid Color'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Tap to change color
                                </div>
                              </div>
                  </button>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-2 border-t border-gray-200">
                        <button
                              type="button"
                              onClick={handleSaveEdit}
                              disabled={isSaving}
                              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsEditMode(false);
                                setEditError(null);
                              }}
                              disabled={isSaving}
                              className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                      ) : isHistoryMode ? (
                        <div className="p-4">
                          {/* Back Button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsHistoryMode(false);
                            }}
                            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                          >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            <span className="text-sm">Back</span>
                          </button>

                          {/* History Title */}
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking History</h3>

                          {/* Scrollable History Timeline */}
                          <div className="max-h-[60vh] overflow-y-auto">
                            {historyLoading ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                            ) : bookingHistory.length === 0 ? (
                              <div className="text-center py-8 text-gray-500 text-sm">
                                No history available
                              </div>
                            ) : (
                              <div className="space-y-4">
                                {bookingHistory.map((item, index) => (
                                  <div key={index} className="flex gap-3">
                                    {/* Timeline Line */}
                                    <div className="flex flex-col items-center">
                                      <div className={`w-2 h-2 rounded-full ${
                                        item.type === 'created' ? 'bg-purple-500' :
                                        item.type === 'status_change' ? 'bg-blue-500' :
                                        item.type === 'milestone' ? 'bg-green-500' :
                                        'bg-gray-400'
                                      }`}></div>
                                      {index < bookingHistory.length - 1 && (
                                        <div className="w-0.5 h-16 bg-gray-200 mt-1"></div>
                  )}
                </div>
                                    
                                    {/* Content */}
                                    <div className="flex-1 pb-4">
                                      <div className="text-sm font-medium text-gray-900">
                                        {item.type === 'created' && (
                                          <>Booking Created</>
                                        )}
                                        {item.type === 'status_change' && (
                                          <>Status changed to <span className="capitalize">{item.status || 'Unknown'}</span></>
                                        )}
                                        {item.type === 'milestone' && item.milestone && (
                                          <>{item.milestone.title || 'Milestone'}</>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        {format(new Date(item.timestamp || item.created_at), 'MMM d, yyyy h:mm a')}
                                      </div>
                                      {item.created_by && (
                                        <div className="text-xs text-gray-400 mt-1">
                                          By {item.created_by}
                                        </div>
                                      )}
                                      {item.type === 'milestone' && item.milestone?.description && (
                                        <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                                          {item.milestone.description}
                                        </div>
                                      )}
                                      {item.type === 'created' && item.description && (
                                        <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                                          {item.description}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                ) : null}
                    </div>
                  </>,
                  document.body
            )}
                </div>
              )}
            </div>
          )}
            
        {/* Right side - Actions (only for desktop or special actions) */}
        {!isMobile && (
          <div className="flex items-center space-x-2">
            {onScheduleFieldQuote && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onScheduleFieldQuote();
                }}
                className="inline-flex items-center justify-center h-8 w-8 text-white bg-blue-600 hover:bg-blue-700 rounded-full shadow-sm active:scale-[0.98] transition-colors"
                title="Schedule Field Quote"
                aria-label="Schedule Field Quote"
              >
                <Plus className="h-5 w-5" strokeWidth={2.5} />
              </button>
            )}
            {onConvertToBooking && !isBooked && (
              <button
                onClick={onConvertToBooking}
                className="inline-flex items-center justify-center w-8 h-8 text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                title="Convert to Booking"
              >
                <CheckCircle2 className="h-4 w-4" />
              </button>
            )}
            {isBooked && (
              <span className="inline-flex items-center justify-center w-8 h-8 text-green-400 bg-green-900/50 rounded-lg border border-green-800/50"
              >
                <CheckCircle2 className="h-4 w-4" />
              </span>
            )}
            {/* Desktop menu button */}
            {canEdit && (
              <div className="relative" ref={desktopMenuRef}>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🔵 Desktop menu button clicked');
                    console.log('🔵 Current isMenuOpen state:', isMenuOpen);
                    console.log('🔵 Current isMobile state:', isMobile);
                    console.log('🔵 Current canEdit state:', canEdit);
                    const newState = !isMenuOpen;
                    console.log('🔵 Setting isMenuOpen to:', newState);
                    setIsMenuOpen(newState);
                    console.log('🔵 State updated, menu should be:', newState ? 'OPEN' : 'CLOSED');
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="inline-flex items-center justify-center w-8 h-8 text-white hover:bg-gray-800 rounded-lg transition-colors"
                  title="More Options"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>

                {/* Desktop: Centered menu with blurred background - Same as mobile */}
                {isMenuOpen && !isMobile && createPortal(
                  <>
                    {/* Backdrop Overlay */}
                    <div
                      className="fixed inset-0"
                      style={{
                        zIndex: overlayBaseZIndex,
                        background: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        opacity: 0,
                        animation: 'fadeIn 0.2s ease-out forwards',
                        cursor: 'pointer',
                      }}
                      onMouseDown={(e) => {
                        console.log('🔵 Backdrop onMouseDown:', {
                          target: e.target,
                          currentTarget: e.currentTarget,
                          isSame: e.target === e.currentTarget,
                          targetTag: (e.target as HTMLElement)?.tagName,
                          targetClass: (e.target as HTMLElement)?.className
                        });
                        // Only close if clicking directly on the backdrop
                        const menuElement = document.querySelector('[data-desktop-menu]');
                        const isClickOnMenu = menuElement?.contains(e.target as Node);
                        console.log('🔵 Backdrop check:', {
                          menuElement: !!menuElement,
                          isClickOnMenu,
                          willClose: e.target === e.currentTarget && !isClickOnMenu
                        });
                        if (e.target === e.currentTarget && !isClickOnMenu) {
                          console.log('🔴 Backdrop closing menu (onMouseDown)');
                          setIsMenuOpen(false);
                          setIsEditMode(false);
                          setIsHistoryMode(false);
                        }
                      }}
                      onClick={(e) => {
                        console.log('🔵 Backdrop onClick:', {
                          target: e.target,
                          currentTarget: e.currentTarget,
                          isSame: e.target === e.currentTarget,
                          targetTag: (e.target as HTMLElement)?.tagName,
                          targetClass: (e.target as HTMLElement)?.className
                        });
                        // Only close if clicking directly on the backdrop
                        const menuElement = document.querySelector('[data-desktop-menu]');
                        const isClickOnMenu = menuElement?.contains(e.target as Node);
                        console.log('🔵 Backdrop check:', {
                          menuElement: !!menuElement,
                          isClickOnMenu,
                          willClose: e.target === e.currentTarget && !isClickOnMenu
                        });
                        if (e.target === e.currentTarget && !isClickOnMenu) {
                          console.log('🔴 Backdrop closing menu (onClick)');
                          setIsMenuOpen(false);
                          setIsEditMode(false);
                          setIsHistoryMode(false);
                        }
                      }}
                    />

                    {/* Centered Menu - iOS Style */}
                    <div
                      data-desktop-menu
                      className="fixed bg-white rounded-[14px] shadow-lg"
                      style={{
                        zIndex: overlayBaseZIndex + 1,
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 'calc(100% - 32px)',
                        maxWidth: '320px',
                        opacity: 0,
                        animation: 'fadeIn 0.2s ease-out 0.05s forwards',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
                        pointerEvents: 'auto',
                      }}
                      onMouseDown={(e) => {
                        console.log('🟢 Menu container onMouseDown:', {
                          target: e.target,
                          currentTarget: e.currentTarget,
                          targetTag: (e.target as HTMLElement)?.tagName,
                          targetClass: (e.target as HTMLElement)?.className
                        });
                        // Always stop propagation to prevent backdrop from closing
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        console.log('🟢 Menu container onClick:', {
                          target: e.target,
                          currentTarget: e.currentTarget,
                          targetTag: (e.target as HTMLElement)?.tagName,
                          targetClass: (e.target as HTMLElement)?.className
                        });
                        // Always stop propagation to prevent backdrop from closing
                        e.stopPropagation();
                      }}
                    >
                      {!isEditMode && !isHistoryMode ? (
                        // Main Menu View
                    <div className="py-1" role="menu" aria-orientation="vertical">
                          {/* Remove from automations toggle */}
                          {onToggleAutomationsDisabled && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  onToggleAutomationsDisabled(!automationsDisabled);
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                                role="menuitemcheckbox"
                                aria-checked={automationsDisabled}
                                style={{ WebkitTapHighlightColor: 'transparent', pointerEvents: 'auto', zIndex: overlayBaseZIndex + 2 }}
                              >
                                <span className={`h-5 w-5 mr-3 rounded-[6px] border flex items-center justify-center flex-shrink-0 ${automationsDisabled ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}>
                                  {automationsDisabled && <Check className="h-3.5 w-3.5 text-white" />}
                                </span>
                                <span className="font-normal">Remove from automations</span>
                              </button>
                              <div className="h-px bg-gray-100 mx-4" />
                            </>
                          )}
                          {/* Edit Booking Button */}
                          {canEdit && status !== 'cancelled' && (
                        <button
                              type="button"
                              onClick={(e) => {
                                console.log('✅ Edit Booking onClick:', {
                                  event: e,
                                  target: e.target,
                                  currentTarget: e.currentTarget,
                                  defaultPrevented: e.defaultPrevented,
                                  isPropagationStopped: false
                                });
                                e.stopPropagation();
                                e.preventDefault();
                                console.log('✅ Edit Booking - setting edit mode');
                                setIsEditMode(true);
                                setEditTitle(title);
                                setEditColor(bookingColor || '#3b82f6');
                                setEditSecondaryColor(bookingSecondaryColor || undefined);
                                console.log('✅ Edit Booking - state updated');
                              }}
                              onMouseDown={(e) => {
                                console.log('✅ Edit Booking onMouseDown');
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                              role="menuitem"
                              style={{ WebkitTapHighlightColor: 'transparent', pointerEvents: 'auto', zIndex: overlayBaseZIndex + 2 }}
                            >
                              <Pencil className="h-5 w-5 mr-3 text-gray-600" />
                              <span className="font-normal">Edit Booking</span>
                            </button>
                          )}
                          {/* Booking History Button */}
                          {bookingId && (
                        <button
                              type="button"
                              onClick={(e) => {
                                console.log('✅ Booking History onClick:', {
                                  event: e,
                                  target: e.target,
                                  currentTarget: e.currentTarget
                                });
                                e.stopPropagation();
                                e.preventDefault();
                                console.log('✅ Booking History - setting history mode');
                                setIsHistoryMode(true);
                                console.log('✅ Booking History - state updated');
                              }}
                              onMouseDown={(e) => {
                                console.log('✅ Booking History onMouseDown');
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                              role="menuitem"
                              style={{ WebkitTapHighlightColor: 'transparent', pointerEvents: 'auto', zIndex: overlayBaseZIndex + 2 }}
                            >
                              <History className="h-5 w-5 mr-3 text-gray-600" />
                              <span className="font-normal">Booking History</span>
                            </button>
                          )}
                          {/* Add Damage Report Button */}
                          {isLiabilityWaiverEnabled && canAccessLiabilityWaiver && onAddDamageReport && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                            setIsMenuOpen(false);
                                setTimeout(() => {
                                  onAddDamageReport();
                                }, 100);
                          }}
                              className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                          role="menuitem"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                              <ClipboardList className={`h-5 w-5 mr-3 ${hasDamageReport ? 'text-green-600' : 'text-red-600'}`} />
                              <span className="font-normal">{hasDamageReport ? 'Edit Damage Report' : 'Add Damage Report'}</span>
                        </button>
                      )}
                          {/* Send/View Liability Waiver Button */}
                          {isLiabilityWaiverEnabled && canAccessLiabilityWaiver && (
                            <>
                              {!liabilityWaiverAgreed && onSendLiabilityWaiver ? (
                        <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                            setIsMenuOpen(false);
                                    setTimeout(() => {
                                      onSendLiabilityWaiver();
                                    }, 100);
                          }}
                                  disabled={isSendingLiabilityWaiver}
                                  className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
                          role="menuitem"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                                  <ClipboardList className="h-5 w-5 mr-3 text-red-600" />
                                  <span className="font-normal">Send Waiver</span>
                        </button>
                              ) : liabilityWaiverPdfUrl ? (
                                <a
                                  href={liabilityWaiverPdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsMenuOpen(false);
                                    setTimeout(() => {
                                      window.open(liabilityWaiverPdfUrl, '_blank');
                                    }, 100);
                                  }}
                                  className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                                  role="menuitem"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  <ClipboardList className="h-5 w-5 mr-3 text-green-600" />
                                  <span className="font-normal">View Waiver</span>
                                </a>
                              ) : null}
                            </>
                          )}
                          {/* Schedule Warranty Button */}
                      {onCreateWarranty && status === 'completed' && (
                        <button
                              onClick={(e) => {
                                e.stopPropagation();
                            setIsMenuOpen(false);
                                setTimeout(() => {
                                  onCreateWarranty();
                                }, 100);
                          }}
                              className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                          role="menuitem"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                              <span className="font-normal">Schedule Warranty</span>
                        </button>
                      )}
                          {/* Unschedule Button */}
                          {onUnschedule && status !== 'cancelled' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsMenuOpen(false);
                                setTimeout(() => {
                                  setIsUnscheduleModalOpen(true);
                                }, 100);
                              }}
                              className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                              role="menuitem"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <CalendarOff className="h-5 w-5 mr-3 text-amber-600" />
                              <span className="font-normal">Unschedule</span>
                            </button>
                          )}
                          {/* Cancel and Delete Buttons */}
                          {(onCancel || onDelete) && (
                            <>
                              <div className="h-[0.5px] bg-gray-200 mx-4 my-1"></div>
                              <div className="flex gap-2 px-4 pb-1">
                      {onCancel && (
                                  <div className="flex-1 relative">
                        <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                            setIsMenuOpen(false);
                                        setTimeout(() => {
                                          setIsCancelModalOpen(true);
                                        }, 100);
                          }}
                                      className={`w-full px-4 py-3.5 text-[17px] text-left flex items-center transition-colors active:bg-gray-100 touch-manipulation relative ${
                            status === 'cancelled' 
                                          ? 'text-blue-600' 
                                          : 'text-red-600'
                          }`}
                          role="menuitem"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                                      <Ban className="h-5 w-5 mr-3" />
                                      <span className="font-normal flex-1">{status === 'cancelled' ? 'Schedule' : 'Cancel'}</span>
                        </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowCancelInfo(!showCancelInfo);
                                        setShowDeleteInfo(false);
                                      }}
                                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      <Info className="h-4 w-4" />
                                    </button>
                                    {showCancelInfo && (
                                      <div className="absolute top-full left-0 right-0 mt-1 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                                        Changes booking status to canceled and booking remains in your calender with the updated status
                                      </div>
                                    )}
                                  </div>
                      )}
                      {onDelete && (
                                  <div className="flex-1 relative">
                        <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                            setIsMenuOpen(false);
                                        setTimeout(() => {
                                          setIsDeleteModalOpen(true);
                                        }, 100);
                          }}
                                      className="w-full px-4 py-3.5 text-[17px] text-left text-red-600 flex items-center transition-colors active:bg-red-50 touch-manipulation relative"
                          role="menuitem"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                                      <Trash2 className="h-5 w-5 mr-3" />
                                      <span className="font-normal flex-1">Delete</span>
                        </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowDeleteInfo(!showDeleteInfo);
                                        setShowCancelInfo(false);
                                      }}
                                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      <Info className="h-4 w-4" />
                                    </button>
                                    {showDeleteInfo && (
                                      <div className="absolute top-full left-0 right-0 mt-1 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                                        Permanently deletes booking
                                      </div>
                      )}
                    </div>
                                )}
                  </div>
                            </>
                          )}
                        </div>
                      ) : isEditMode ? (
                        // Edit Mode View
                        <div className="p-4 space-y-4">
                          {/* Back Button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsEditMode(false);
                              setEditError(null);
                            }}
                            className="flex items-center text-gray-600 hover:text-gray-900 mb-2"
                          >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            <span className="text-sm">Back</span>
                          </button>

                          {/* Error Message */}
                          {editError && (
                            <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-sm">
                              {editError}
              </div>
            )}

                          {/* Title Input */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Booking Title
                            </label>
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              className="w-full h-11 px-3 rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-base"
                              placeholder="Enter booking title"
                              autoFocus
                            />
          </div>

                          {/* Color Picker */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Booking Color
                            </label>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsColorPickerOpen(true);
                              }}
                              className="flex items-center gap-3 w-full p-3 rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                            >
                              {/* Current Color Display */}
                              <div className="w-12 h-12 rounded-lg border-2 border-gray-300 overflow-hidden shadow-sm flex-shrink-0">
                                {editSecondaryColor ? (
                                  <div className="w-full h-full flex">
                                    <div 
                                      className="w-1/2 h-full"
                                      style={{ backgroundColor: editColor }}
                                    />
                                    <div 
                                      className="w-1/2 h-full"
                                      style={{ backgroundColor: editSecondaryColor }}
                                    />
                                  </div>
                                ) : (
                                  <div 
                                    className="w-full h-full"
                                    style={{ backgroundColor: editColor }}
                                  />
                                )}
                              </div>
                              <div className="flex-1 text-left">
                                <div className="text-sm font-medium text-gray-900">
                                  {editSecondaryColor ? 'Two-tone Color' : 'Solid Color'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Tap to change color
                                </div>
                              </div>
                            </button>
        </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-2 border-t border-gray-200">
                            <button
                              type="button"
                              onClick={handleSaveEdit}
                              disabled={isSaving}
                              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsEditMode(false);
                                setEditError(null);
                              }}
                              disabled={isSaving}
                              className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : isHistoryMode ? (
                        // History Mode View
                        <div className="p-4">
                          {/* Back Button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsHistoryMode(false);
                            }}
                            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                          >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            <span className="text-sm">Back</span>
                          </button>

                          {/* History Title */}
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking History</h3>

                          {/* Scrollable History Timeline */}
                          <div className="max-h-[60vh] overflow-y-auto">
                            {historyLoading ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                              </div>
                            ) : bookingHistory.length === 0 ? (
                              <div className="text-center py-8 text-gray-500 text-sm">
                                No history available
                              </div>
                            ) : (
                              <div className="space-y-4">
                                {bookingHistory.map((item, index) => (
                                  <div key={index} className="flex gap-3">
                                    {/* Timeline Line */}
                                    <div className="flex flex-col items-center">
                                      <div className={`w-2 h-2 rounded-full ${
                                        item.type === 'created' ? 'bg-purple-500' :
                                        item.type === 'status_change' ? 'bg-blue-500' :
                                        item.type === 'milestone' ? 'bg-green-500' :
                                        'bg-gray-400'
                                      }`}></div>
                                      {index < bookingHistory.length - 1 && (
                                        <div className="w-0.5 h-16 bg-gray-200 mt-1"></div>
                                      )}
                                    </div>
                                    
                                    {/* Content */}
                                    <div className="flex-1 pb-4">
                                      <div className="text-sm font-medium text-gray-900">
                                        {item.type === 'created' && (
                                          <>Booking Created</>
                                        )}
                                        {item.type === 'status_change' && (
                                          <>Status changed to <span className="capitalize">{item.status || 'Unknown'}</span></>
                                        )}
                                        {item.type === 'milestone' && item.milestone && (
                                          <>{item.milestone.title || 'Milestone'}</>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        {format(new Date(item.timestamp || item.created_at), 'MMM d, yyyy h:mm a')}
                                      </div>
                                      {item.created_by && (
                                        <div className="text-xs text-gray-400 mt-1">
                                          By {item.created_by}
                                        </div>
                                      )}
                                      {item.type === 'milestone' && item.milestone?.description && (
                                        <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                                          {item.milestone.description}
                                        </div>
                                      )}
                                      {item.type === 'created' && item.description && (
                                        <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                                          {item.description}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </>,
                  document.body
                )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setError(null);
        }}
        onConfirm={async () => {
          try {
            if (!onDelete) {
              throw new Error('Delete function not available');
            }
            setDeleteLoading(true);
            setError(null);
            await onDelete();
            setIsDeleteModalOpen(false);
          } catch (err) {
            console.error('Error deleting booking:', err);
            setError('Failed to delete booking');
          } finally {
            setDeleteLoading(false);
          }
        }}
        title="Delete Booking"
        message={`Are you sure you want to delete booking #${bookingNumber}? This action cannot be undone.`}
        loading={deleteLoading}
        error={error}
      />

      <DeleteConfirmationModal
        isOpen={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false);
          setError(null);
        }}
        onConfirm={async () => {
          try {
            if (!onCancel) {
              throw new Error('Cancel function not available');
            }
            setCancelLoading(true);
            setError(null);
            await onCancel();
            setIsCancelModalOpen(false);
          } catch (err) {
            console.error('Error updating booking:', err);
            setError('Failed to update booking');
          } finally {
            setCancelLoading(false);
          }
        }}
        title={status === 'cancelled' ? 'Schedule Booking' : 'Cancel Booking'}
        message={
          status === 'cancelled'
            ? `Are you sure you want to schedule booking #${bookingNumber}? This will make the booking active again.`
            : `Are you sure you want to cancel booking #${bookingNumber}? The booking will remain visible but marked as canceled.`
        }
        loading={cancelLoading}
        confirmText={status === 'cancelled' ? 'Schedule' : 'Cancel'}
        confirmButtonClass={
          status === 'cancelled' 
            ? 'bg-blue-600 hover:bg-blue-700' 
            : 'bg-red-600 hover:bg-red-700'
        }
        error={error}
      />

      <DeleteConfirmationModal
        isOpen={isUnscheduleModalOpen}
        onClose={() => {
          setIsUnscheduleModalOpen(false);
          setError(null);
        }}
        onConfirm={async () => {
          try {
            if (!onUnschedule) {
              throw new Error('Unschedule function not available');
            }
            setUnscheduleLoading(true);
            setError(null);
            await onUnschedule();
            setIsUnscheduleModalOpen(false);
          } catch (err) {
            console.error('Error unscheduling booking:', err);
            setError('Failed to unschedule booking');
          } finally {
            setUnscheduleLoading(false);
          }
        }}
        title="Unschedule Booking"
        message={`Are you sure you want to unschedule booking #${bookingNumber}? Its date and time will be cleared and it will move to the unscheduled list.`}
        loading={unscheduleLoading}
        confirmText="Unschedule"
        confirmButtonClass="bg-amber-600 hover:bg-amber-700"
        error={error}
      />
    </>
  );
}